import React from 'react';
import { ClassRecord } from '../../data/classes';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface ClassCalendarProps {
  classes: ClassRecord[];
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  onClassClick: (classRecord: ClassRecord) => void;
  onDayClick: (date: string, dayClasses: ClassRecord[]) => void;
}
export function ClassCalendar({
  classes,
  currentMonth,
  onMonthChange,
  onClassClick,
  onDayClick
}: ClassCalendarProps) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const days = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const blanks = Array.from({
    length: firstDayOfMonth
  }, (_, i) => i);
  const prevMonth = () => {
    onMonthChange(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    onMonthChange(new Date(year, month + 1, 1));
  };
  const goToToday = () => {
    onMonthChange(new Date());
  };
  const todayStr = new Date().toISOString().split('T')[0];
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-40">
      {/* Calendar Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-41">
        <h3 className="text-lg font-bold text-gray-900" data-id="element-42">
          {currentMonth.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        })}
        </h3>
        <div className="flex items-center space-x-2" data-id="element-43">
          <button onClick={goToToday} className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-purple-500" data-id="element-44">
            Today
          </button>
          <div className="flex border border-gray-300 rounded-md overflow-hidden" data-id="element-45">
            <button onClick={prevMonth} className="p-1.5 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-purple-500" aria-label="Previous month" data-id="element-46">
              <ChevronLeft className="w-5 h-5" data-id="element-47" />
            </button>
            <div className="w-px bg-gray-300" data-id="element-48"></div>
            <button onClick={nextMonth} className="p-1.5 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-purple-500" aria-label="Next month" data-id="element-49">
              <ChevronRight className="w-5 h-5" data-id="element-50" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-100" data-id="element-51">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-52">
            {day}
          </div>)}
      </div>

      <div className="grid grid-cols-7 bg-gray-200 gap-px" data-id="element-53">
        {blanks.map(blank => <div key={`blank-${blank}`} className="bg-white min-h-[120px] p-2 opacity-50" data-id="element-54"></div>)}

        {days.map(day => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayClasses = classes.filter(c => c.date === dateStr).sort((a, b) => {
          // Simple time sort
          return a.startTime.localeCompare(b.startTime);
        });
        const isToday = dateStr === todayStr;
        return <div key={day} className={`bg-white min-h-[120px] p-2 flex flex-col transition-colors ${isToday ? 'bg-brand-purple-50/30' : ''} hover:bg-gray-50 cursor-pointer`} onClick={() => onDayClick(dateStr, dayClasses)} role="button" tabIndex={0} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onDayClick(dateStr, dayClasses);
          }
        }} aria-label={`${new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric'
        })}, ${dayClasses.length} classes`} data-id="element-55">
              <div className="flex justify-between items-start mb-1" data-id="element-56">
                <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-brand-purple-600 text-white' : 'text-gray-700'}`} data-id="element-57">
                  {day}
                </span>
              </div>

              <div className="flex-1 flex flex-col gap-1 overflow-hidden" data-id="element-58">
                {dayClasses.slice(0, 2).map(cls => {
              const isJon = cls.instructorGroup === 'Jon Kichen';
              return <div key={cls.id} onClick={e => {
                e.stopPropagation();
                onClassClick(cls);
              }} className={`text-xs p-1.5 rounded truncate border cursor-pointer hover:shadow-sm transition-shadow ${cls.status === 'cancelled' ? 'bg-gray-100 text-gray-500 border-gray-200 line-through' : 'bg-white border-gray-200 text-gray-800'}`} title={cls.title} data-id="element-59">
                      <div className="flex items-center gap-1 mb-0.5" data-id="element-60">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isJon ? 'bg-brand-purple-600' : 'bg-brand-orange-500'}`} data-id="element-61"></span>
                        <span className="font-semibold truncate" data-id="element-62">
                          {cls.startTime.replace(':00', '')}
                        </span>
                        <span className="text-[10px] uppercase text-gray-500 bg-gray-100 px-1 rounded" data-id="element-63">
                          {cls.format === 'Zoom' ? 'Z' : 'IP'}
                        </span>
                      </div>
                      <div className="truncate" data-id="element-64">{cls.title}</div>
                    </div>;
            })}
                {dayClasses.length > 2 && <div className="text-xs text-brand-purple-600 font-medium mt-1 text-center hover:underline" data-id="element-65">
                    + {dayClasses.length - 2} more
                  </div>}
              </div>
            </div>;
      })}

        {/* Fill remaining cells to complete the grid */}
        {Array.from({
        length: (7 - (blanks.length + days.length) % 7) % 7
      }).map((_, i) => <div key={`end-blank-${i}`} className="bg-white min-h-[120px] p-2 opacity-50" data-id="element-66"></div>)}
      </div>
    </div>;
}